import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import CommandBar from "@/components/CommandBar";
import AgentPanel from "@/components/AgentPanel";
import HelpChatWidget from "@/components/HelpChatWidget";

// Pages
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import CRM from "@/pages/CRM";
import Pitch from "@/pages/Pitch";
import Pricing from "@/pages/Pricing";
import Workflows from "@/pages/Workflows";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Emails from "@/pages/Emails";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import DemandIntel from "@/pages/DemandIntel";
import IncomeInsurance from "@/pages/IncomeInsurance";
import SharedResults from "@/pages/SharedResults";
import ForBusiness from "@/pages/ForBusiness";
import ForOperators from "@/pages/ForOperators";
import ForLaidOffTech from "@/pages/ForLaidOffTech";
import Careers from "@/pages/Careers";
import CaseStudies from "@/pages/CaseStudies";
import AdminBeta from "@/pages/AdminBeta";
import MagicLogin from "@/pages/MagicLogin";
import Businesses from "@/pages/Businesses";
import Knowledge from "@/pages/Knowledge";
import { HelpCenter, HelpArticle } from "@/pages/HelpCenter";
import BusinessOnboarding from "@/pages/BusinessOnboarding";
import FirstTimeJourney from "@/pages/FirstTimeJourney";
import Revenue from "@/pages/Revenue";
import HubDashboard from "@/pages/HubDashboard";
import Marketplace from "@/pages/Marketplace";
import AgentMonitor from "@/pages/AgentMonitor";
import ClientPortal from "@/pages/ClientPortal";
import Operations from "@/pages/Operations";
import CRMSettings from "@/pages/CRMSettings";
import Analytics from "@/pages/Analytics";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/shared/:shareId" element={<SharedResults />} />
      <Route path="/portal" element={<ClientPortal />} />
      <Route path="/for-business" element={<ForBusiness />} />
      <Route path="/for-operators" element={<ForOperators />} />
      <Route path="/for-laid-off-tech" element={<ForLaidOffTech />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/help/:slug" element={<HelpArticle />} />
      <Route path="/auth/magic" element={<MagicLogin />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Auth mode="login" />
          </PublicRoute>
        } 
      />
      <Route 
        path="/signup" 
        element={
          <PublicRoute>
            <Auth mode="signup" />
          </PublicRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/jobs" 
        element={
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crm" 
        element={
          <ProtectedRoute>
            <CRM />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pitch" 
        element={
          <ProtectedRoute>
            <Pitch />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workflows" 
        element={
          <ProtectedRoute>
            <Workflows />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workflow" 
        element={
          <ProtectedRoute>
            <WorkflowBuilder />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/demand" 
        element={
          <ProtectedRoute>
            <DemandIntel />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/income-calculator" 
        element={
          <ProtectedRoute>
            <IncomeInsurance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/hub" 
        element={
          <ProtectedRoute>
            <HubDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/marketplace" 
        element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/beta-users" 
        element={
          <ProtectedRoute>
            <AdminBeta />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/businesses" 
        element={
          <ProtectedRoute>
            <Businesses />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/businesses/:bizId/knowledge" 
        element={
          <ProtectedRoute>
            <Knowledge />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/businesses/onboard" 
        element={
          <ProtectedRoute>
            <BusinessOnboarding />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <FirstTimeJourney />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/revenue" 
        element={
          <ProtectedRoute>
            <Revenue />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agents" 
        element={
          <ProtectedRoute>
            <AgentMonitor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/operations" 
        element={
          <ProtectedRoute>
            <Operations />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/crm-settings" 
        element={
          <ProtectedRoute>
            <CRMSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/emails" 
        element={
          <ProtectedRoute>
            <Emails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Global command bar for authenticated users
const GlobalCommandBar = () => {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <CommandBar />
      <AgentPanel />
      <HelpChatWidget />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <GlobalCommandBar />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
